import { Box, SxProps, Theme, Typography } from '@mui/material';
import { ReactElement, ReactNode } from 'react';
import ReactMarkdown from 'react-markdown';

export interface MarkdownProps {
  content: string | undefined;
  sx?: SxProps<Theme>;
}

const components = {
  p: ({ children }: { children?: ReactNode }): ReactElement => (
    <Typography component="p" variant="body1" gutterBottom>
      {children}
    </Typography>
  ),
  h1: ({ children }: { children?: ReactNode }): ReactElement => (
    <Typography component="h1" variant="h4" gutterBottom>
      {children}
    </Typography>
  ),
  h2: ({ children }: { children?: ReactNode }): ReactElement => (
    <Typography component="h2" variant="h5" gutterBottom>
      {children}
    </Typography>
  ),
  h3: ({ children }: { children?: ReactNode }): ReactElement => (
    <Typography component="h3" variant="h6" gutterBottom>
      {children}
    </Typography>
  ),
  ul: ({ children }: { children?: ReactNode }): ReactElement => (
    <Box component="ul" sx={{ pl: 3, my: 1 }}>
      {children}
    </Box>
  ),
  ol: ({ children }: { children?: ReactNode }): ReactElement => (
    <Box component="ol" sx={{ pl: 3, my: 1 }}>
      {children}
    </Box>
  ),
  li: ({ children }: { children?: ReactNode }): ReactElement => (
    <Typography component="li" variant="body1">
      {children}
    </Typography>
  ),
  a: ({
    href,
    children,
  }: {
    href?: string;
    children?: ReactNode;
  }): ReactElement => (
    <Box
      component="a"
      href={href}
      sx={{ color: 'primary.main', textDecoration: 'underline' }}
      target={href?.startsWith('http') ? '_blank' : undefined}
      rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined}
    >
      {children}
    </Box>
  ),
  strong: ({ children }: { children?: ReactNode }): ReactElement => (
    <Box component="strong" sx={{ fontWeight: 700 }}>
      {children}
    </Box>
  ),
  em: ({ children }: { children?: ReactNode }): ReactElement => (
    <Box component="em" sx={{ fontStyle: 'italic' }}>
      {children}
    </Box>
  ),
};

export function Markdown({ content, sx }: MarkdownProps): ReactElement {
  return (
    <Box sx={sx}>
      <ReactMarkdown components={components}>{content ?? ''}</ReactMarkdown>
    </Box>
  );
}
